<template>
  <div class="overflow-hidden">
    <section class="cp-page py-4">
      <header-help :is-loading="isLoading"></header-help>
      
      <!-- Background animated orbs using Stella brand colors -->
      <div class="cp-bg-orb cp-bg-orb--1"></div>
      <div class="cp-bg-orb cp-bg-orb--2"></div>

      <div class="container position-relative my-auto" style="z-index: 1;">
        <global-message></global-message>
        <div class="row justify-content-center">
          <div class="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5">
            <div class="cp-content-card p-4 p-md-5 text-center">
              
              <!-- Celebration Icon with Glow -->
              <div class="avatar-glow-wrapper mb-3">
                <div class="d-inline-flex align-items-center justify-content-center" 
                     style="width: 72px; height: 72px; border-radius: 50%; background: linear-gradient(135deg, rgba(229,59,185,0.2), rgba(112,213,194,0.2)); border: 2px solid #E53BB9; box-shadow: 0 0 35px rgba(229, 59, 185, 0.4);">
                  <span style="font-size: 28px;">🎉</span>
                </div>
              </div>

              <h2 class="text-white font-weight-700 mb-1">You're In!</h2>
              <p class="text-white-50 font-size-14 mb-3">
                Welcome to <strong class="text-white">@{{ inviteeName }}</strong>'s Inner Circle
              </p>

              <!-- Perks Checklist -->
              <div class="perk-checklist mb-4">
                <div class="perk-item">
                  <div class="perk-icon"><i class="fa fa-check"></i></div>
                  <span>Exclusive content feed unlocked</span>
                </div>
                <div class="perk-item">
                  <div class="perk-icon"><i class="fa fa-check"></i></div>
                  <span>Direct 1-on-1 messaging line active</span>
                </div>
              </div>

              <!-- Profile Finalization Form (if profile not yet complete) -->
              <div v-if="needsProfileSetup" class="text-start border-top pt-4 mt-3" style="border-color: rgba(255,255,255,0.08) !important;">
                <h5 class="text-white font-weight-600 mb-1">Finalize Your Profile</h5>
                <p class="text-white-50 font-size-13 mb-3">Set up your account details to manage your subscription and chat.</p>

                <div class="form-group mb-3">
                  <label class="text-white font-size-13 mb-2 font-weight-500">Your Full Name</label>
                  <required-field v-if="isRequired('name')"/>
                  <input type="text" placeholder="Enter your full name"
                    class="form-control cp-input"
                    v-model="user.name">
                </div>

                <div class="form-group mb-3">
                  <label class="text-white font-size-13 mb-2 font-weight-500">Account Password</label>
                  <required-field v-if="isRequired('password')"/>
                  <div class="position-relative">
                    <input placeholder="Create a secure password"
                      type="password" class="form-control cp-input"
                      id="password" v-model="user.password" style="padding-right: 42px;">
                    <span class="position-absolute cursor-pointer text-muted font-size-16" 
                          style="right: 14px; top: 12px;"
                          @click="showPassword('password')">
                      <i id="show_id" class="fa fa-eye text-white-50"></i>
                      <i id="hide_id" class="fa fa-eye-slash text-white-50 d-none"></i>
                    </span>
                  </div>
                </div>

                <div class="form-group mb-3">
                  <label class="text-white font-size-13 mb-2 font-weight-500">Country</label>
                  <required-field v-if="isRequired('countryCode')"/>
                  <searchable-country-select 
                    v-model="user.countryCode" 
                    :countries="countries" 
                    placeholder="Select Country" />
                </div>

                <div class="form-group mb-4">
                  <label class="text-white font-size-13 mb-2 font-weight-500">Phone Number</label>
                  <required-field v-if="isRequired('phone')"/>
                  <country-phone-input
                    v-model="user.phone"
                    :country-code="user.countryCode"
                    :is-submit="isSubmit"
                  />
                </div>

                <button type="button"
                        class="cp-btn-primary w-100"
                        @click="finishProfile()">
                  Complete Profile & Continue <i class="fa fa-arrow-right ms-2"></i>
                </button>
              </div>

              <!-- Single Continue button if profile is already complete -->
              <div v-else class="mt-4">
                <button type="button"
                        class="cp-btn-primary px-4"
                        @click="onContinue()">
                  Continue to Creator Hub <i class="fa fa-arrow-right ms-2"></i>
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>

    <model-customer-custom-request-submit
      ref="customRequestConfirm"
      @savedCustomRequest="(customRequest) => {
        $refs.customRequestDone.show(customRequest);
      }"
      @submittedCustomRequestOption="(customRequest) => {
        $refs.customRequestOption.show(customRequest, getUser().id);
      }"/>

    <model-customer-custom-request-option-info
      ref="customRequestOption"
      @savedCustomRequestOption="(customRequest) => {
        if(customRequest.status === CR_STATUS_ACCEPTED){
          $refs.customRequestAccepted.show(customRequest);
        } else if(customRequest.status === CR_STATUS_READY) {
          $refs.customRequestDone.show(customRequest, true);
        }
      }"
      @back="() => {
        $refs.customRequestConfirm.simpleShow();
      }"
    />

    <model-customer-custom-request-accepted ref="customRequestAccepted"/>

    <model-customer-custom-request-done
      ref="customRequestDone" @doneCustomRequest="() => {
        $router.replace({name: 'CustomerCustomRequestList'});
      }"/>
  </div>
</template>

<script>
import GlobalMessage from "@/components/module/shared/GlobalMessage";
import LoadingModal from "@/components/module/shared/LoadingModal";
import {mapActions, mapGetters} from "vuex";
import HeaderHelp from "@/components/module/influencer/shared/HeaderHelp";
import {customerAccountMixin} from "@/mixin/customer-account/customer-account-mixin";
import {isSuffixCustomRequest} from "@/common/comm-service";
import {showPassword} from "@/common/Utils";
import {required} from "vuelidate/lib/validators";
import {SERVICE_IDENTIY, SERVICE_PAYMENT} from "@/properties/micro-service";
import RequiredField from "@/components/module/shared/RequiredField";
import SearchableCountrySelect from "@/components/module/shared/SearchableCountrySelect";
import CountryPhoneInput from "@/components/module/shared/CountryPhoneInput";
import {isPhoneValidForCountry} from "@/common/phone-validation";
import ModelCustomerCustomRequestConfirm
  from "@/components/module/customer/custom-request/ModelCustomerCustomRequestConfirm";
import ModelCustomerCustomRequestDone from "@/components/module/customer/custom-request/ModelCustomerCustomRequestDone";
import ModelCustomerCustomRequestSubmit
  from "@/components/module/customer/custom-request/ModelCustomerCustomRequestSubmit";
import ModelCustomerCustomRequestOptionInfo
  from "@/components/module/customer/custom-request/ModelCustomerCustomRequestOptionInfo";
import {CR_STATUS_ACCEPTED, CR_STATUS_READY} from "@/properties/custom-request-status";
import ModelCustomerCustomRequestAccepted
  from "@/components/module/customer/custom-request/ModelCustomerCustomRequestAccepted";
import titleMixin from "../../../../mixin/title-mixin";
import {paymentPaid, SUBSCRIPTION} from "@/common/analytics-service";

export default {
  name: "SubscriptionComplete",
  mixins: [customerAccountMixin, titleMixin],
  title: 'Subscription complete',
  components: {
    GlobalMessage, LoadingModal, HeaderHelp, RequiredField, SearchableCountrySelect,
    CountryPhoneInput,
    ModelCustomerCustomRequestConfirm,
    ModelCustomerCustomRequestDone,
    ModelCustomerCustomRequestSubmit,
    ModelCustomerCustomRequestOptionInfo,
    ModelCustomerCustomRequestAccepted
  },
  data() {
    return {
      inviteeName: '',
      user: {},
      isSubmit: false,
      countries: [],
      CR_STATUS_ACCEPTED,
      CR_STATUS_READY
    }
  },
  validations() {
    return {
      user: {
        name: {required},
        countryCode: {required},
        password: {required},
        phone: {
          required,
          validPhone: (val) => isPhoneValidForCountry(val, this.user ? this.user.countryCode : 'US')
        },
      }
    };
  },
  computed: {
    ...mapGetters(['getInvitee', 'getRedirectedFrom', 'getUser']),
    isSuffixCustomRequest() {
      return isSuffixCustomRequest(this.getRedirectedFrom);
    },
    needsProfileSetup() {
      return !this.user.name || !this.user.hasPassword;
    }
  },
  created() {
    this.inviteeName = this.getInvitee.name;
    this.user = Object.assign({}, this.getUser);
    if (!this.user.countryCode) {
      this.user.countryCode = 'US';
    }
    this.loadCountries();

    let urlPrefix = this.getInvitee.name;
    if (this.$route.query.status === 'success') {
      if (this.$route.query.clientReferenceId) {
        let clientReferenceId = this.$route.query.clientReferenceId;
        let URL = `${SERVICE_PAYMENT}/api/v1/stripe/transactions/${clientReferenceId}`;
        this.putData(URL, {}, () => {}, () => {
          this.$router.replace({ name: 'Platform', params: { urlPrefix } });
        });
      }
    } else if (this.$route.query.status === 'failure') {
      this.$router.replace({ name: 'Platform', params: { urlPrefix } });
    }
    const currentUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
    window.history.replaceState({ path: currentUrl }, '', currentUrl);
  },
  mounted() {
    paymentPaid(this, SUBSCRIPTION, this.getInvitee);
  },
  methods: {
    ...mapActions(['setMessage', 'setUser']),
    showPassword,
    isRequired(attrib) {
      if (!this.isSubmit || !this.$v.user || !this.$v.user[attrib]) return false;
      const field = this.$v.user[attrib];
      if (field.required !== undefined && !field.required) return true;
      if (field.validPhone !== undefined && !field.validPhone) return true;
      return false;
    },
    loadCountries() {
      this.getUnSecureData('identity/api/v1/lookup/countries', (data) => {
        this.countries = data;
      });
    },
    finishProfile() {
      this.isSubmit = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      this.patchData(`${SERVICE_IDENTIY}/api/v1/customers/${this.user.id}`, this.user, (data) => {
        this.$store.dispatch('setMessage', 'Profile saved successfully.');
        this.user = data;
        this.setUser(data);
        this.onContinue();
      });
    },
    onContinue() {
      if (this.isSuffixCustomRequest) {
        this.submitCustomRequest(this.getInvitee);
      } else {
        this.$router.replace({name: 'TipPayment'});
      }
    },
    submitCustomRequest(influencer) {
      this.$refs.customRequestConfirm.show(influencer.id, this.getUser.id, influencer.customRequestMinPrice);
    },
  }
}
</script>
